/**
 * The two surfaces of the 3-D view, as custom deck.gl layers on the installed
 * @deck.gl/core 9.3.10 and luma.gl (no mesh-layers package is installed).
 *
 * Both are lit by the page's LightingEffect (ambient, sun and fill; phong material,
 * per fragment), darkened by the cast-shadow field traced toward the same sun
 * (mesh.ts castShadows), fade toward the sky colour with distance, and apply the
 * vertical exaggeration in the vertex shader.
 *
 * Positions are [lon, lat, metres] with 64-bit precision (LNGLAT).
 */

import { Layer, phongMaterial, picking, project32, type DefaultProps, type LayerProps, type UpdateParameters } from '@deck.gl/core'
import type { Texture } from '@luma.gl/core'
import { Model } from '@luma.gl/engine'

import type { TerrainMesh, WaterMesh } from './mesh'

export interface SceneUniforms {
  exaggeration: number
  useTexture: number
  fogNear: number
  fogFar: number
  fogColor: [number, number, number]
  mixT: number
  time: number
  wetThreshold: number
  sink: number
  cellM: number
  hasVectors: number
  origin: [number, number]
  metersPerDeg: [number, number]
}

const block = /* glsl */ `\
layout(std140) uniform sceneUniforms {
  float exaggeration;
  float useTexture;
  float fogNear;
  float fogFar;
  vec3 fogColor;
  float mixT;
  float time;
  float wetThreshold;
  float sink;
  float cellM;
  float hasVectors;
  vec2 origin;
  vec2 metersPerDeg;
} scene;
`

const sceneModule = {
  name: 'scene',
  vs: block,
  fs: block,
  uniformTypes: {
    exaggeration: 'f32',
    useTexture: 'f32',
    fogNear: 'f32',
    fogFar: 'f32',
    fogColor: 'vec3<f32>',
    mixT: 'f32',
    time: 'f32',
    wetThreshold: 'f32',
    sink: 'f32',
    cellM: 'f32',
    hasVectors: 'f32',
    origin: 'vec2<f32>',
    metersPerDeg: 'vec2<f32>',
  },
} as const

// ---------------------------------------------------------------- terrain

const terrainVs = /* glsl */ `#version 300 es
#define SHADER_NAME scene-terrain-vs
in vec3 positions;
in vec3 positions64Low;
in vec2 gradients;
in vec2 texCoords;
in vec4 colors;
in float texWeight;
in float shade;
out float vShade;
out vec2 vTexCoord;
out vec4 vColor;
out float vTexWeight;
out vec3 vNormal;
out vec3 vPos;
out vec3 vCamera;
void main(void) {
  geometry.worldPosition = positions;
  vCamera = project.cameraPosition;
  vec3 p = vec3(positions.xy, positions.z * scene.exaggeration);
  vec3 p64 = vec3(positions64Low.xy, 0.0);
  gl_Position = project_position_to_clipspace(p, p64, vec3(0.0), geometry.position);
  vec3 n = normalize(vec3(-gradients.x * scene.exaggeration, -gradients.y * scene.exaggeration, 1.0));
  geometry.normal = project_normal(n);
  geometry.pickingColor = vec3(1.0, 0.0, 0.0);
  vNormal = geometry.normal;
  vPos = geometry.position.xyz;
  vTexCoord = texCoords;
  vTexWeight = texWeight;
  vShade = shade;
  DECKGL_FILTER_GL_POSITION(gl_Position, geometry);
  vColor = vec4(colors.rgb, colors.a * layer.opacity);
  DECKGL_FILTER_COLOR(vColor, geometry);
}
`

const terrainFs = /* glsl */ `#version 300 es
#define SHADER_NAME scene-terrain-fs
precision highp float;
uniform sampler2D imageryTexture;
in vec2 vTexCoord;
in vec4 vColor;
in float vTexWeight;
in float vShade;
in vec3 vNormal;
in vec3 vPos;
in vec3 vCamera;
out vec4 fragColor;
void main(void) {
  vec3 base = vColor.rgb;
  if (scene.useTexture > 0.5 && vTexWeight > 0.5) {
    base = texture(imageryTexture, vTexCoord).rgb;
  }
  vec3 lit = lighting_getLightColor(base, vCamera, vPos, normalize(vNormal));
  lit *= 1.0 - 0.55 * vShade;
  float fog = smoothstep(scene.fogNear, scene.fogFar, length(vPos - vCamera));
  fragColor = vec4(mix(lit, scene.fogColor, fog * 0.7), vColor.a);
  geometry.uv = vec2(0.0);
  DECKGL_FILTER_COLOR(fragColor, geometry);
}
`

type TerrainProps = LayerProps & {
  mesh: TerrainMesh | null
  texture: Texture | HTMLCanvasElement | null
  scene: SceneUniforms
  material: unknown
}

const terrainDefaults: DefaultProps<TerrainProps> = {
  mesh: { type: 'object', value: null, compare: true },
  texture: { type: 'image', value: null, async: true },
  scene: { type: 'object', value: null as unknown as SceneUniforms, compare: true },
  material: { type: 'object', value: true },
}

abstract class MeshLayer<P extends LayerProps & { mesh: { indices: Uint32Array } | null }> extends Layer<P> {
  declare state: { model?: Model | null; dummy?: Texture }

  abstract attributeSpec(): Record<string, { size: number; key: string; fp64?: boolean }>

  initializeState() {
    const manager = this.getAttributeManager()!
    const spec: Record<string, object> = {
      indices: { size: 1, isIndexed: true, noAlloc: true, update: (a: { value: unknown }) => (a.value = this.props.mesh?.indices) },
    }
    for (const [name, s] of Object.entries(this.attributeSpec())) {
      spec[name] = {
        size: s.size,
        ...(s.fp64 ? { type: 'float64', fp64: this.use64bitPositions() } : {}),
        noAlloc: true,
        update: (a: { value: unknown }) => (a.value = (this.props.mesh as Record<string, unknown> | null)?.[s.key]),
      }
    }
    manager.add(spec as never)
    this.state = {
      model: null,
      dummy: this.context.device.createTexture({ width: 1, height: 1, data: new Uint8Array([0, 0, 0, 0]) }),
    }
  }

  updateState(params: UpdateParameters<this>) {
    super.updateState(params)
    const { props, oldProps, changeFlags } = params
    if (!this.state.model || changeFlags.extensionsChanged) {
      this.state.model?.destroy()
      this.state.model = new Model(this.context.device, {
        ...(this as unknown as { getShaders: () => object }).getShaders(),
        id: this.props.id,
        bufferLayout: this.getAttributeManager()!.getBufferLayouts(),
        topology: 'triangle-list',
        isInstanced: false,
      })
      this.getAttributeManager()!.invalidateAll()
    }
    if (props.mesh !== oldProps.mesh) {
      this.getAttributeManager()!.invalidateAll()
      this.state.model.setVertexCount(props.mesh?.indices.length ?? 0)
    }
  }

  finalizeState(context: Parameters<Layer['finalizeState']>[0]) {
    super.finalizeState(context)
    this.state.model?.destroy()
    this.state.dummy?.destroy()
  }
}

export class TerrainSurfaceLayer extends MeshLayer<TerrainProps> {
  static layerName = 'TerrainSurfaceLayer'
  static defaultProps = terrainDefaults

  getShaders() {
    return super.getShaders({ vs: terrainVs, fs: terrainFs, modules: [project32, picking, phongMaterial, sceneModule] })
  }

  attributeSpec() {
    return {
      positions: { size: 3, key: 'positions', fp64: true },
      gradients: { size: 2, key: 'gradients' },
      texCoords: { size: 2, key: 'texCoords' },
      colors: { size: 4, key: 'colors' },
      texWeight: { size: 1, key: 'texWeight' },
      shade: { size: 1, key: 'shade' },
    }
  }

  draw() {
    const { model, dummy } = this.state
    if (!model || !this.props.mesh || !this.props.scene) return
    model.shaderInputs.setProps({
      scene: { ...this.props.scene, imageryTexture: (this.props.texture as Texture | null) ?? dummy },
    } as never)
    model.draw(this.context.renderPass)
  }
}

// ---------------------------------------------------------------- water

const waterVs = /* glsl */ `#version 300 es
#define SHADER_NAME scene-water-vs
in vec3 positions;
in vec3 positions64Low;
in vec2 depths;
in vec4 grads;
in vec4 colorA;
in vec4 colorB;
in vec4 flows;
in float role;
in float shade;
out float vShade;
out vec3 vPos;
out vec3 vNormal;
out vec4 vColor;
out float vWet;
out vec2 vFlow;
out vec2 vMeters;
out vec3 vCamera;
void main(void) {
  geometry.worldPosition = positions;
  vCamera = project.cameraPosition;
  float d = mix(depths.x, depths.y, scene.mixT);
  float wet = step(scene.wetThreshold, d);
  float k = scene.exaggeration;
  float ground = positions.z * k;
  float top = wet > 0.5 ? ground + d * k : ground - scene.sink;
  float z = role > 1.5 ? ground - scene.sink : top;
  vec3 p = vec3(positions.xy, z);
  vec3 p64 = vec3(positions64Low.xy, 0.0);
  gl_Position = project_position_to_clipspace(p, p64, vec3(0.0), geometry.position);
  vec2 g = mix(grads.xy, grads.zw, scene.mixT);
  vec3 n = normalize(vec3(-g.x * k, -g.y * k, 1.0));
  geometry.normal = project_normal(n);
  geometry.pickingColor = vec3(1.0, 0.0, 0.0);
  vNormal = geometry.normal;
  vPos = geometry.position.xyz;
  // a skirt edge whose top is dry has no wall: collapse it
  vWet = wet;
  vShade = shade;
  vFlow = mix(flows.xy, flows.zw, scene.mixT);
  vMeters = (positions.xy - scene.origin) * scene.metersPerDeg;
  vec4 c = mix(colorA, colorB, scene.mixT);
  float alpha = 0.55 + 0.4 * smoothstep(0.05, 6.0, d);
  if (role > 0.5) {
    c.rgb *= 0.72;
    alpha = 0.9;
  }
  DECKGL_FILTER_GL_POSITION(gl_Position, geometry);
  vColor = vec4(c.rgb, alpha * layer.opacity);
  DECKGL_FILTER_COLOR(vColor, geometry);
}
`

const waterFs = /* glsl */ `#version 300 es
#define SHADER_NAME scene-water-fs
precision highp float;
in vec3 vPos;
in vec3 vNormal;
in vec4 vColor;
in float vWet;
in vec2 vFlow;
in vec2 vMeters;
in vec3 vCamera;
in float vShade;
out vec4 fragColor;
void main(void) {
  if (vWet <= 0.0) discard;
  vec3 n = normalize(vNormal);
  vec3 base = vColor.rgb;
  float L = max(scene.cellM, 1.0);
  if (scene.hasVectors > 0.5) {
    // Streaks travel along the stored depth-averaged velocity vector at this point.
    float speed = length(vFlow);
    if (speed > 0.02) {
      vec2 dir = vFlow / speed;
      float phase = dot(vMeters, dir) / (2.0 * L) - scene.time * (0.2 + min(speed, 6.0) * 0.25);
      float streak = pow(0.5 + 0.5 * sin(6.28318 * phase), 8.0);
      base += streak * 0.10;
      n = normalize(n + vec3(dir * streak * 0.18, 0.0));
    }
  } else {
    // No velocity field stored: a slow standing shimmer with no direction.
    float s = sin(vMeters.x / (1.7 * L)) * sin(vMeters.y / (2.3 * L)) * sin(scene.time * 0.5);
    float s2 = sin((vMeters.x + vMeters.y) / (3.1 * L)) * sin(scene.time * 0.37 + 1.1);
    n = normalize(n + vec3(0.05 * s, 0.05 * s2, 0.0));
  }
  vec3 lit = lighting_getLightColor(base, vCamera, vPos, n);
  lit *= 1.0 - 0.5 * vShade;
  // A liquid surface reflects the sky more the flatter it is seen (Schlick's Fresnel term).
  vec3 view = normalize(vCamera - vPos);
  float fresnel = 0.04 + 0.96 * pow(1.0 - max(dot(n, view), 0.0), 5.0);
  lit = mix(lit, scene.fogColor * 1.15, fresnel * 0.6);
  float fog = smoothstep(scene.fogNear, scene.fogFar, length(vPos - vCamera));
  fragColor = vec4(mix(lit, scene.fogColor, fog * 0.7), vColor.a);
  geometry.uv = vec2(0.0);
  DECKGL_FILTER_COLOR(fragColor, geometry);
}
`

type WaterProps = LayerProps & {
  mesh: WaterMesh | null
  scene: SceneUniforms
  material: unknown
}

const waterDefaults: DefaultProps<WaterProps> = {
  mesh: { type: 'object', value: null, compare: true },
  scene: { type: 'object', value: null as unknown as SceneUniforms, compare: true },
  material: { type: 'object', value: true },
}

export class WaterSurfaceLayer extends MeshLayer<WaterProps> {
  static layerName = 'WaterSurfaceLayer'
  static defaultProps = waterDefaults

  getShaders() {
    return super.getShaders({ vs: waterVs, fs: waterFs, modules: [project32, picking, phongMaterial, sceneModule] })
  }

  attributeSpec() {
    return {
      positions: { size: 3, key: 'positions', fp64: true },
      depths: { size: 2, key: 'depths' },
      grads: { size: 4, key: 'grads' },
      colorA: { size: 4, key: 'colorA' },
      colorB: { size: 4, key: 'colorB' },
      flows: { size: 4, key: 'flows' },
      role: { size: 1, key: 'role' },
      shade: { size: 1, key: 'shade' },
    }
  }

  draw() {
    const { model } = this.state
    if (!model || !this.props.mesh || !this.props.scene || !this.props.mesh.indices.length) return
    model.shaderInputs.setProps({ scene: this.props.scene } as never)
    model.draw(this.context.renderPass)
  }
}
